import { onPageLoad } from '../modules/page_lifecycle.js';

/**
 * vanilla-cookieconsent Built-in API Methods
 * 
 * The cookieConsent object provides these built-in methods for dynamic cookie management:
 * 
 * 1. window.cookieConsent.accept(categories)
 *    - Accept specific cookie categories
 *    - Example: window.cookieConsent.accept(['analytics', 'advertising'])
 * 
 * 2. window.cookieConsent.deny(category)
 *    - Deny/disable a specific cookie category
 *    - Example: window.cookieConsent.deny('analytics')
 * 
 * 3. window.cookieConsent.eraseCookies(category, cookieNames)
 *    - Erase cookies for a specific category
 *    - Example: window.cookieConsent.eraseCookies('analytics', ['_ga', '_gid'])
 * 
 * 4. window.cookieConsent.allowedCategory(category)
 *    - Check if a category is currently allowed
 *    - Returns: boolean
 *    - Example: window.cookieConsent.allowedCategory('analytics')
 * 
 * 5. window.cookieConsent.getUserPreferences()
 *    - Get current user preferences
 *    - Returns: object with categories array
 *    - Example: window.cookieConsent.getUserPreferences()
 * 
 * 6. window.cookieConsent.showSettings()
 *    - Show the cookie settings modal
 *    - Example: window.cookieConsent.showSettings()
 * 
 * 7. window.cookieConsent.hideSettings()
 *    - Hide the cookie settings modal
 *    - Example: window.cookieConsent.hideSettings()
 * 
 * Usage Examples:
 * - Disable analytics: window.cookieConsent.deny('analytics')
 * - Enable advertising: window.cookieConsent.accept(['advertising'])
 * - Check status: window.cookieConsent.allowedCategory('analytics')
 * - Get all preferences: window.cookieConsent.getUserPreferences()
 */

var COOKIE_CONSENT_OPTIONS = {
  revision: 3,
  current_lang: "en",
  autoclear_cookies: true,
  page_scripts: true,
  onAccept: ({ categories }) => {
    updateGtag(categories);
  },
  onChange: ({ categories }) => {
    updateGtag(categories);
  },
  onFirstAction: ({ accepted_categories }) => {
    updateGtag(accepted_categories);
  },
  languages: {
    en: {
      consent_modal: {
        title: "We value your privacy",
        description:
          "We use some essential cookies to make this service work. We also use non-essential cookies to analyse site traffic, serve personalised ads or content, and improve our products.<br /> Where you or your company hold a paid Membership, we use Member State cookies to provide annual usage reports. Member State cookies are unable to be turned off at an individual level; only the Paying Member or Lead Contact of your company’s Membership may opt the whole company out (i.e., everyone on your user list).<br /> You can read more about how your data is handled in our <a href=\"/privacy-policy\">privacy policy</a> and <a href=\"/cookie_policy\">cookie policy</a>.<br /> We will not disclose your personal information to your competitors or third parties except in the following cases: <ul><li>With Service Providers, Contractors, and Agents</li><li>With the users' employer or the account licence holder</li><li>Under circumstances required by law</li></ul><br/> Please note that by rejecting all non-essential cookies some services, such as remembering your user preferences, may be affected. By clicking ‘Accept All’, you consent to our use of cookies. <button type=\"button\" data-cc=\"c-settings\" class=\"cc-link\">Customise selection</button>",
        primary_btn: {
          text: "Accept all",
          role: "accept_all",
        },
        secondary_btn: {
          text: "Reject all",
          role: "accept_necessary",
        },
      },
      settings_modal: {
        title: "Cookie preferences",
        save_settings_btn: "Save settings",
        accept_all_btn: "Accept all",
        reject_all_btn: "Reject all",
        close_btn_label: "Close",
        cookie_table_headers: [
          { col2: "Name" },
          { col1: "Cookie" },
          { col3: "Expires" },
          { col4: "Purpose" },
        ],
        blocks: [
          {
            title: "Cookie usage",
            description:
              "For each category you can choose to opt-in/out whenever you want. For more details related to cookies please read the full <a href=\"/cookie_policy\" class=\"cc-link\" target=\"_blank\">cookie policy</a>.",
          },
          {
            title: "Strictly Necessary (Essential Cookies)",
            description:
              "Essential for websites to provide simple functions and to provide the user with the services they have requested.",
            toggle: {
              value: "necessary",
              enabled: true,
              readonly: false,
            },
            cookie_table: [
              {
                col1: "Zendesk",
                col2: "__cfruid",
                col3: "Session",
                col4: "This cookie is a part of the services provided by Cloudflare - Including load-balancing, deliverance of website content and serving DNS connection for website operators.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "Li_gc",
                col3: "6 months",
                col4: "Stores cookie consent preferences.",
                is_regex: false,
              },
              {
                col1: "Widget mediator zopim",
                col2: "AWSALBCORS",
                col3: "7 days",
                col4: "Provides load balancing functionality.",
                is_regex: false,
              },
              {
                col1: "Vimeo",
                col2: "__cf_bm",
                col3: "30 minutes",
                col4: "Read and filter requests from bots.",
                is_regex: false,
              },
              {
                col1: "Calendly",
                col2: "__cf_bm",
                col3: "30 minutes",
                col4: "Read and filter requests from bots.",
                is_regex: false,
              },
              {
                col1: "Google",
                col2: "_GRECAPTCHA",
                col3: "6 months",
                col4: "Provides spam protection.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_gwi2018_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_WaterData_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_desaldata_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_new_ultrapuremicro_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_leading_utilities2_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_global_water_leaders_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "_ultrapure_water_rails_session",
                col3: "Session",
                col4: "Allows the app to store the current session.",
                is_regex: false,
              },
              {
                col1: ".nr-data.net",
                col2: "JSESSIONID",
                col3: "Session",
                col4: "Used to maintain an anonymous user session by the server.",
                is_regex: false,
              },
            ],
          },
          {
            title: "Performance / Analytical cookies (Internal)",
            description:
              "Performance cookies are used to understand how users navigate and interact with websites. For example, analytic cookies may collect how many users are on a specific website, how long a user stays, and which pages they visit. These cookies are required for logged-in users.",
            toggle: {
              value: "analytics-internal",
              enabled: true,
              readonly: true,
            },
            cookie_table: [
              {
                col1: "GWI",
                col2: "ahoy_track",
                col3: "7 days",
                col4: "This is set by GWI for analytics measurement.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "ahoy_visitor",
                col3: "2 years",
                col4: "This is set by GWI for analytics measurement.",
                is_regex: false,
              },
              {
                col1: "GWI",
                col2: "ahoy_visit",
                col3: "4 hours",
                col4: "This is set by GWI for analytics measurement.",
                is_regex: false,
              },
            ],
          },
          {
            title: "Performance / Analytical Cookies",
            description:
              "Monitor site performance and to understand user behaviour in a non-identifiable way. Used to improve the user experience.",
            toggle: {
              value: "analytics",
              enabled: true,
              readonly: true,
            },
            cookie_table: [
              {
                col1: "Google Analytics",
                col2: "_gid",
                col3: "1 day",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "Google Analytics",
                col2: "^_ga",
                col3: "2 years",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: true,
              },
              {
                col1: "Google Analytics",
                col2: "_gat",
                col3: "1 minute",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "Google Analytics",
                col2: "_utma",
                col3: "2 years",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "Google Analytics",
                col2: "_utmc",
                col3: "Session",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "Google Analytics",
                col2: "_utmz",
                col3: "6 months, 2 days",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "Google Analytics",
                col2: "_utmt",
                col3: "10 minutes",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "Google Analytics",
                col2: "_utmb",
                col3: "30 mintues",
                col4: "These cookies are used to collect information about how visitors use our websites. We use the information to compile reports and to support website improvements. For example, the number of visitors to the website and pages they have visited. <a href=\"https://support.google.com/analytics/answer/6004245\">Read Google’s overview of privacy and safeguarding data for more information</a>.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "AnalyticsSyncHistory",
                col3: "1 month",
                col4: "Used to store information about the time a sync took place with the lms_analytics cookie",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "lms_analytics",
                col3: "1 month",
                col4: "Used to identify LinkedIn Members off LinkedIn for advertising",
                is_regex: false,
              },
              {
                col1: "YouTube",
                col2: "YSC",
                col3: "Session",
                col4: "To store and track interactions.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "lidc",
                col3: "1 day",
                col4: "Provide load balancing functionality.",
                is_regex: false,
              },
              {
                col1: "LinkedIn (Oribi)",
                col2: "Ln_or",
                col3: "1 day",
                col4: "Used to determine if Oribi analytics can be carried out on a specific domain",
                is_regex: false,
              },
            ],
          },
          {
            title: "Targeting / Advertising Cookies",
            description:
              "Track visitor web activity to provide targeted advertisements.",
            toggle: {
              value: "advertising",
              enabled: false,
              readonly: false,
            },
            cookie_table: [
              {
                col1: "YouTube",
                col2: "VISITOR_INFO1_LIVE",
                col3: "6 months",
                col4: "Provide bandwidth estimations.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "UserMatchHistory",
                col3: "30 days",
                col4: "Provide ad delivery or retargeting.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "bcookie",
                col3: "1 year",
                col4: "Store browser details.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "Bscookie",
                col3: "1 year",
                col4: "Store logged in users.",
                is_regex: false,
              },
              {
                col1: "LinkedIn / Microsoft MSN",
                col2: "ln_or",
                col3: "1 day",
                col4: "Used to determine if Oribi analytics can be carried out on a specific domain.",
                is_regex: false,
              },
              {
                col1: "Google DoubleClick",
                col2: "IDE",
                col3: "1 year",
                col4: "Provide ad delivery or retargeting.",
                is_regex: false,
              },
              {
                col1: "Google DoubleClick",
                col2: "Test_cookie",
                col3: "15 minutes",
                col4: "Used by Google DoubleClick to check that cookies can be set.",
                is_regex: false,
              },
              {
                col1: "Google AdSense",
                col2: "_gcl_au",
                col3: "3 months",
                col4: "Store and track conversions.",
                is_regex: false,
              },
              {
                col1: "Universal Analytics (Google)",
                col2: "^_gat_gtag_UA",
                col3: "1 minute",
                col4: "Store a unique user ID.",
                is_regex: true,
              },
              {
                col1: "Hubspot",
                col2: "hubspotutk",
                col3: "6 months",
                col4: "Store a unique user ID.",
                is_regex: false,
              },
            ],
          },
          {
            title: "Functionality Cookies",
            description:
              "Enhances the performance of a website and remembers user preferences.",
            toggle: {
              value: "functionality",
              enabled: false,
              readonly: false,
            },
            cookie_table: [
              {
                col1: "Zopim Live Chat",
                col2: "_zlcmid",
                col3: "1 year",
                col4: "Store a unique user ID for our live chat feature.",
                is_regex: false,
              },
              {
                col1: "Vimeo",
                col2: "vuid",
                col3: "2 years",
                col4: "Store the user's usage history.",
                is_regex: false,
              },
              {
                col1: "Atlassian Jira Servicedesk",
                col2: "Ajs_anonymous_id",
                col3: "1 year",
                col4: "Store last visit.",
                is_regex: false,
              },
              {
                col1: "Wufoo.com",
                col2: "Ep201",
                col3: "30 mins",
                col4: "This cookie is used to embed forms on our website.",
                is_regex: false,
              },
            ],
          },
        ],
      },
    },
  },
};

var cookiesCategories = {
  analyticsInternal: "analytics-internal",
  analytics: "analytics"
};

// Flag to prevent recursive calls when programmatically updating cookies
var isProgrammaticUpdate = false;

/* Helper functions to avoid repeating the same ga code */
function updateGtag(categories) { 
  // If cookies should be disabled, filter out the disabled categories before updating gtag
  var disableAnalyticsCookies = shouldDisableCookies();
  var categoriesToDisable = disableAnalyticsCookies ? Object.values(cookiesCategories) : [];
  var filteredCategories = categories.filter(function(cat) {
    return categoriesToDisable.indexOf(cat) === -1;
  });
  
  // Only call disableCookieCategories if we're not already in a programmatic update
  // This prevents recursive calls when disableCookieCategories triggers onChange
  if (disableAnalyticsCookies && !isProgrammaticUpdate) {
    disableCookieCategories(filteredCategories);
    // Reset ahoy unless analytics-internal is still enabled
  }
  
  if (!filteredCategories.includes("analytics-internal") && window.ahoy) {
    window.ahoy.reset();
  }

  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }

  // Use filteredCategories instead of categories to ensure disabled categories are not granted
  gtag("consent", "update", {
    "ad_storage": filteredCategories.includes("advertising") ? "granted" : "denied",
    "ad_user_data": filteredCategories.includes("advertising") ? "granted" : "denied",
    "ad_personalization": filteredCategories.includes("advertising") ? "granted" : "denied",
    "analytics_storage": filteredCategories.includes("analytics") ? "granted" : "denied"
  });
}

/**
 * Extract cookie names from blocks configuration for a given category
 * @param {string} category - The category to find cookies for
 * @returns {string[]} Array of cookie names
 */
function getCookiesFromBlocks(category) {
  var block = COOKIE_CONSENT_OPTIONS.languages.en.settings_modal.blocks.find(function(b) {
    return b.toggle && b.toggle.value === category && b.cookie_table;
  });
  return block ? block.cookie_table.map(function(c) { return c.col2 ? c.col2.replace(/^\^/, "") : null; }).filter(Boolean) : [];
}

/**
 * Get list of companies that should have cookies disabled
 * @returns {string[]} Array of company names
 */
function getDisabledCompanies() {
  return window.disableTrackingCompanies || [];
}

/**
 * Check if cookies should be disabled for the current account
 * @returns {boolean} True if the account is in the disabled companies list
 */
function shouldDisableCookies() {
  var disabledCompanies = getDisabledCompanies();
  return disabledCompanies.some(function(x) {
    return window.cookie_banner_account && window.cookie_banner_account.toLowerCase().includes(x.toLowerCase());
  });
}

/**
 * Modify COOKIE_CONSENT_OPTIONS to disable and set readonly: false for specified categories
 * @param {string[]} categories - Array of category values to modify
 */
function modifyCookieCategoriesConfig(categories) {
  var blocks = COOKIE_CONSENT_OPTIONS.languages.en.settings_modal.blocks;
  categories.forEach(function(category) {
    var block = blocks.find(function(b) {
      return b.toggle && b.toggle.value === category;
    });
    if (block && block.toggle) {
      block.toggle.enabled = false;
      block.toggle.readonly = false;
    }
  });
}

/**
 * Disable cookie categories by erasing cookies, updating preferences, and disabling UI toggles
 * @param {string[]} categoriesToAccept - Categories that should remain enabled (already filtered)
 */
function disableCookieCategories(categoriesToAccept) { 
  // Set flag to prevent recursive calls
  isProgrammaticUpdate = true;
  
  // Get list of categories to disable from cookiesCategories object
  var categoriesToDisable = Object.values(cookiesCategories);
  
  // Use provided categories or get from preferences as fallback
  var currentCategories = categoriesToAccept || (window.cookieConsent.getUserPreferences().categories || []).filter(function(cat) {
    return categoriesToDisable.indexOf(cat) === -1;
  });

  // Erase cookies for each category we're disabling
  // Extract cookie names from blocks configuration and delete them
  categoriesToDisable.forEach(function(category) {
    var cookieNames = getCookiesFromBlocks(category);

    if (cookieNames.length) {
      window.cookieConsent.eraseCookies(category, cookieNames);
    }
  });
  
  // Accept only the remaining categories (this effectively disables the filtered ones)
  // Since vanilla-cookieconsent doesn't have a deny() method, we use accept() with filtered list
  if (currentCategories.length) {
    window.cookieConsent.accept(currentCategories);
  }

  window.ahoy.reset();
  
  // Clear the flag after a short delay to allow onChange to process
  setTimeout(function() {
    isProgrammaticUpdate = false;
  }, 100);
  
  // Disable UI toggles for each category by finding checkbox by value
  [3, 4].forEach(function(childIndex) {
    document.querySelector("#cc--main #s-bl>div:nth-child(" + childIndex + ") .c-tg").classList.add("c-ro");
    document.querySelector("#cc--main #s-bl>div:nth-child(" + childIndex + ") .c-tgl").disabled = true;
  });
}

function initLoggedOut() {
  window.cookieConsent.run(COOKIE_CONSENT_OPTIONS);
}

function initLoggedIn() {
  function categoriesToAccept() {
    function addIfAccepted(categories, category) {
      if (window.cookieConsent.allowedCategory(category)) {
        categories.push(category);
      }
    }

    var categories = [];

    addIfAccepted(categories, "analytics");
    addIfAccepted(categories, "advertising");
    addIfAccepted(categories, "functionality");
    addIfAccepted(categories, "analytics-internal");

    return categories;
  }

  var disableCookies = shouldDisableCookies();

  // Modify COOKIE_CONSENT_OPTIONS to disable categories if needed
  if (disableCookies) {
    modifyCookieCategoriesConfig(Object.values(cookiesCategories));
  }

  window.cookieConsent.run(COOKIE_CONSENT_OPTIONS);

  if (disableCookies) {
    disableCookieCategories();
  } else {
    categoriesToAccept();
  }
}

let cookieConsentInitialized = false;

function markCookieConsentPermanent() {
  document.getElementById('cc--main')?.setAttribute('data-turbo-permanent', '');
}

function initCookieBanner() {
  if (cookieConsentInitialized) return;
  cookieConsentInitialized = true;

  window.cookieConsent = window.initCookieConsent();
  if (window.cookie_banner_account) {
    initLoggedIn();
  } else {
    initLoggedOut();
  }
  markCookieConsentPermanent();
}

onPageLoad(initCookieBanner);
